import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AddIcon from "@mui/icons-material/Add";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { DynamicFieldRenderer } from "../../../../components/DynamicFieldRenderer/DynamicFieldRenderer";
import { useLanguages } from "../../../../context/LanguagesContext";
import { usePortal } from "../../../../context/PortalsContext";
import { useToast } from "../../../../context/ToastContext";
import { getAdvancedUserAttributesDDL } from "../../../../services/api/apiClient";
import {
  AdvancedDdlResult,
  AdvancedSearchFilter,
  AdvancedSearchSelection,
  BooleanOperator,
  DateOperator,
  ListOperator,
  NumericOperator,
  StringOperator,
} from "../../../../services/api/types/users/users";
import { brand, gray } from "../../../../theme/themePrimitives";
import { numberToOrdinalWord } from "../../../../utils/helpers";
import { extractErrorMessage } from "../../../../utils/errorUtils";
import useScreenSize from "../../../../hooks/useScreenSize";

interface RuleBuilderFilterProps {
  showFilter: boolean;
  setShowFilter: React.Dispatch<React.SetStateAction<boolean>>;
  selections: AdvancedSearchSelection[];
  handleChange: <K extends keyof AdvancedSearchFilter>(
    selectionIndex: number,
    filterIndex: number,
    field: K,
    value: AdvancedSearchFilter[K],
    groupHeader?: string,
    filterGroup?: string,
    initialValue?: string,
  ) => void;
  addRow: (selectionIndex: number) => void;
  removeRow: (selectionIndex: number, filterIndex: number) => void;
  getValuesForAttribute: (
    attribute: string,
    operator:
      | StringOperator
      | NumericOperator
      | DateOperator
      | BooleanOperator
      | ListOperator
      | "",
    value: string,
    selectionIndex: number,
    filterIndex: number,
  ) => void;
  handleSearch: (chips: string[]) => void;
  addSelection: () => void;
  removeSelection: (selectionIndex: number) => void;
  clearAll: () => void;
  loading: boolean;
}

export const RuleBuilderFilter: React.FC<RuleBuilderFilterProps> = ({
  showFilter,
  setShowFilter,
  selections,
  handleChange,
  addRow,
  removeRow,
  getValuesForAttribute,
  handleSearch,
  addSelection,
  removeSelection,
  clearAll,
  loading,
}) => {
  const [groupedAttributes, setGroupedAttributes] =
    useState<AdvancedDdlResult[]>();
  const [fetchLoading, setFetchLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [openDatepickerIndex, setOpenDatepickerIndex] = useState<string | null>(
    null,
  );
  const [openStartDatepicker, setOpenStartDatepicker] = useState(false);
  const [openEndDatepicker, setOpenEndDatepicker] = useState(false);

  const { portals } = usePortal();
  const { languages } = useLanguages();
  const { showToast } = useToast();
  const { isMobile } = useScreenSize();

  const hasFilterAnyValue = selections.some((s) =>
    s.filters.some(
      (f) => f.attribute?.trim() || f.operator?.trim() || f.value?.trim(),
    ),
  );

  useEffect(() => {
    if (!showFilter || groupedAttributes !== undefined) return;
    const fetch = async () => {
      try {
        setFetchLoading(true);
        if (portals.selectedPortal) {
          const response: AdvancedDdlResult[] =
            await getAdvancedUserAttributesDDL(
              portals.selectedPortal.portalUid,
              {
                languageCode: languages.selectedLanguage,
                assignmentFields: true,
              },
            );
          setGroupedAttributes(response);
        }
      } catch (error: any) {
        showToast(
          extractErrorMessage(error, "Failed to fetch attributes"),
          "error",
        );
      } finally {
        setFetchLoading(false);
      }
    };
    fetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showFilter]);

  const getAttributeDataType = (attributeKey: string): string | null => {
    if (!groupedAttributes) return null;
    for (const group of groupedAttributes) {
      const item = group.items.find((i) => i.attributeKey === attributeKey);
      if (item) return item.dataType.toLowerCase();
    }
    return null;
  };

  const getAttributeName = (attributeKey: string): string => {
    if (!groupedAttributes) return attributeKey;
    for (const group of groupedAttributes) {
      const item = group.items.find((i) => i.attributeKey === attributeKey);
      if (item) return item.attributeName || attributeKey;
    }
    return attributeKey;
  };

  const getAttributeOptions = (
    attributeKey: string,
  ): { key: string; value: string }[] => {
    if (!groupedAttributes) return [];
    for (const group of groupedAttributes) {
      const item = group.items.find((i) => i.attributeKey === attributeKey);
      if (item && Array.isArray((item as any).result)) {
        return (item as any).result as { key: string; value: string }[];
      }
    }
    return [];
  };

  const getAttributeOperator = (attributeKey: string): any => {
    if (!groupedAttributes) return [];
    for (const group of groupedAttributes) {
      const item = group.items.find((i) => i.attributeKey === attributeKey);
      if (item) {
        switch (item.dataType.toLowerCase()) {
          case "string":
          case "text":
            return [
              ["IN", StringOperator.IN],
              ["NOT_IN", StringOperator.NOT_IN],
              ["CONTAINS", StringOperator.CONTAINS],
              ["NOT_CONTAINS", StringOperator.NOT_CONTAINS],
              ["STARTS_WITH", StringOperator.STARTS_WITH],
              ["ENDS_WITH", StringOperator.ENDS_WITH],
            ];
          case "number":
          case "integer":
          case "numeric":
            return NumericOperator;
          case "date":
          case "datetime":
            return [
              ["EQUAL", DateOperator.EQUAL],
              ["NOT_EQUAL", DateOperator.NOT_EQUAL],
              ["LESS_THAN", DateOperator.LESS_THAN],
              ["GREATER_THAN", DateOperator.GREATER_THAN],
              ["GREATER_THAN_EQUAL", DateOperator.GREATER_THAN_EQUAL],
              ["LESS_THAN_EQUAL", DateOperator.LESS_THAN_EQUAL],
              ["BETWEEN", DateOperator.BETWEEN],
            ];
          case "boolean":
            return BooleanOperator;
          case "list":
            return ListOperator;
          default:
            return [
              ["IN", StringOperator.IN],
              ["NOT_IN", StringOperator.NOT_IN],
              ["CONTAINS", StringOperator.CONTAINS],
              ["NOT_CONTAINS", StringOperator.NOT_CONTAINS],
              ["STARTS_WITH", StringOperator.STARTS_WITH],
              ["ENDS_WITH", StringOperator.ENDS_WITH],
            ];
        }
      }
    }
    return [];
  };

  const getEnumLabel = (key: string, dataType?: string): string => {
    if (dataType === "date" || dataType === "datetime") {
      const labels: Record<string, string> = {
        EQUAL: "Equals",
        NOT_EQUAL: "Does not equal",
        LESS_THAN: "Is before",
        GREATER_THAN: "Is after",
        GREATER_THAN_EQUAL: "Is on or after",
        LESS_THAN_EQUAL: "Is on or before",
        BETWEEN: "Between",
      };
      return labels[key] ?? formatKey(key);
    }
    if (dataType === "string" || dataType === "text") {
      const labels: Record<string, string> = {
        IN: "Is in",
        NOT_IN: "Is not in",
        CONTAINS: "Contains",
        NOT_CONTAINS: "Does not contain",
        STARTS_WITH: "Starts with",
        ENDS_WITH: "Ends with",
      };
      return labels[key] ?? formatKey(key);
    }
    return formatKey(key);
  };

  const formatKey = (key: string) =>
    key
      .replace(/_/g, " ")
      .toLowerCase()
      .replace(/^./, (c) => c.toUpperCase());

  const getOperatorLabel = (
    attributeKey: string,
    operatorValue: string,
  ): string => {
    const ops = getAttributeOperator(attributeKey);
    const dataType = getAttributeDataType(attributeKey) ?? undefined;
    if (Array.isArray(ops)) {
      const found = (ops as [string, string][]).find(
        ([, v]) => v === operatorValue,
      );
      return found ? getEnumLabel(found[0], dataType) : operatorValue;
    }
    const entry = Object.entries(ops ?? {}).find(
      ([, v]) => v === operatorValue,
    );
    return entry ? getEnumLabel(entry[0], dataType) : operatorValue;
  };

  const resolveValueLabel = (
    attributeKey: string,
    rawValue: string,
  ): string => {
    const options = getAttributeOptions(attributeKey);
    if (options.length === 0) return rawValue;
    const ids = rawValue
      .split(/[|,]/)
      .map((v) => v.trim())
      .filter(Boolean);
    const labels = ids.map(
      (id) => options.find((o) => o.key === id)?.value ?? id,
    );
    return labels.join("|");
  };

  const buildChips = (): string[] =>
    selections
      .map((selection) => {
        const parts = selection.filters
          .filter((f) => f.attribute.trim())
          .map((f) => {
            const attrName = getAttributeName(f.attribute);
            const opLabel = getOperatorLabel(f.attribute, f.operator);
            const resolved = resolveValueLabel(f.attribute, f.value);
            return `${attrName} ${opLabel.toLowerCase()} "${resolved}"`;
          });
        if (parts.length === 0) return null;
        return parts.length === 1 ? parts[0] : `(${parts.join(" AND ")})`;
      })
      .filter((chip): chip is string => chip !== null);

  const clearFieldError = (key: string) => {
    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const handleApply = () => {
    setFieldErrors({});
    const errors: Record<string, string> = {};

    selections.forEach((selection, si) => {
      selection.filters.forEach((filter, fi) => {
        if (!filter.attribute.trim())
          errors[`${si}-${fi}-attribute`] = "Attribute is required";
        if (!filter.operator.trim())
          errors[`${si}-${fi}-operator`] = "Operator is required";
        const dataType = getAttributeDataType(filter.attribute);
        const effectiveValue =
          dataType === "boolean" && !filter.value ? "false" : filter.value;
        if (!effectiveValue?.trim())
          errors[`${si}-${fi}-value`] = "Value is required";
      });
    });

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    handleSearch(buildChips());
  };

  const handleClose = () => {
    setFieldErrors({});
    setShowFilter(false);
  };

  // Flat list of all attribute options for the attribute dropdown
  const allAttributeOptions =
    groupedAttributes?.flatMap((g) =>
      g.items.map((item) => ({
        value: item.attributeKey,
        label: item.attributeName,
        groupHeader: g.groupHeader,
        filterGroup: item.filterGroup,
        dataType: item.dataType,
      })),
    ) ?? [];

  return (
    <Drawer
      anchor="right"
      open={showFilter}
      onClose={handleClose}
      disableEnforceFocus
      slotProps={{
        paper: {
          sx: {
            backgroundColor: "white",
            boxSizing: "border-box",
            width: isMobile ? "100%" : "477px",
            p: 0,
          },
        },
      }}
    >
      {fetchLoading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="100%"
        >
          <CircularProgress size={60} />
        </Box>
      ) : (
        <Box
          display="flex"
          flexDirection="column"
          height="100%"
          overflow="hidden"
        >
          {/* Header */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            px={2}
            py={1.5}
          >
            <Typography variant="h6" fontWeight={600}>
              Filters (Rule builder)
            </Typography>
            <IconButton onClick={handleClose} size="small">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <Divider />

          {/* Scrollable body */}
          <Box flex={1} overflow="auto" p={2}>
            {groupedAttributes && groupedAttributes.length > 0 && (
              <Stack spacing={2}>
                {selections.map((selection, si) => (
                  <React.Fragment key={si}>
                    {/* Selection header */}
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Typography variant="subtitle1" fontWeight={600}>
                        Selection {si + 1}
                      </Typography>
                      {selections.length > 1 && (
                        <IconButton
                          size="small"
                          onClick={() => removeSelection(si)}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>

                    {/* Filter cards */}
                    <Stack spacing={2}>
                      {selection.filters.map((filter, fi) => (
                        <Box
                          key={fi}
                          sx={{
                            border: `1px solid ${gray[300]}`,
                            borderRadius: "12px",
                            overflow: "hidden",
                          }}
                        >
                          {/* Card header */}
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            px={2}
                            py={1.5}
                          >
                            <Typography variant="subtitle2" fontWeight={600}>
                              {numberToOrdinalWord(fi + 1)} filter
                            </Typography>
                            {selection.filters.length > 1 && (
                              <IconButton
                                size="small"
                                onClick={() => removeRow(si, fi)}
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            )}
                          </Box>
                          <Divider />

                          <Stack spacing={2} p={2}>
                            {/* Attribute + Operator row */}
                            <Box
                              display="grid"
                              gridTemplateColumns="minmax(0, 3fr) minmax(0, 2fr)"
                              gap={1}
                            >
                              {/* Attribute */}
                              <Stack spacing={0.5}>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  Attribute
                                </Typography>
                                <Select
                                  size="small"
                                  value={filter.attribute}
                                  displayEmpty
                                  IconComponent={ExpandMoreIcon}
                                  onChange={(e) => {
                                    const selected = allAttributeOptions.find(
                                      (o) => o.value === e.target.value,
                                    );
                                    const dataType =
                                      selected?.dataType?.toLowerCase();
                                    handleChange(
                                      si,
                                      fi,
                                      "attribute",
                                      e.target.value,
                                      selected?.groupHeader,
                                      selected?.filterGroup,
                                      dataType === "boolean"
                                        ? "false"
                                        : undefined,
                                    );
                                    clearFieldError(`${si}-${fi}-attribute`);
                                  }}
                                  renderValue={(val) =>
                                    val ? (
                                      (allAttributeOptions.find(
                                        (o) => o.value === val,
                                      )?.label ?? val)
                                    ) : (
                                      <Box
                                        component="span"
                                        sx={{ color: gray[400] }}
                                      >
                                        Select
                                      </Box>
                                    )
                                  }
                                  sx={{ borderRadius: "8px" }}
                                  error={!!fieldErrors[`${si}-${fi}-attribute`]}
                                >
                                  {groupedAttributes.map((group) => [
                                    <MenuItem
                                      key={`header-${group.groupHeader}`}
                                      disabled
                                      sx={{
                                        fontWeight: 600,
                                        fontSize: 12,
                                        color: gray[500],
                                        opacity: "1 !important",
                                      }}
                                    >
                                      {group.groupHeader}
                                    </MenuItem>,
                                    ...group.items.map((item) => (
                                      <MenuItem
                                        key={item.attributeKey}
                                        value={item.attributeKey}
                                        sx={{ pl: 3 }}
                                      >
                                        {item.attributeName}
                                      </MenuItem>
                                    )),
                                  ])}
                                </Select>
                                {fieldErrors[`${si}-${fi}-attribute`] && (
                                  <Typography variant="caption" color="error">
                                    {fieldErrors[`${si}-${fi}-attribute`]}
                                  </Typography>
                                )}
                              </Stack>

                              {/* Operator */}
                              <Stack spacing={0.5}>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  Operator
                                </Typography>
                                <Select
                                  size="small"
                                  value={filter.operator}
                                  displayEmpty
                                  disabled={!filter.attribute}
                                  IconComponent={ExpandMoreIcon}
                                  onChange={(e) => {
                                    handleChange(
                                      si,
                                      fi,
                                      "operator",
                                      e.target.value,
                                    );
                                    clearFieldError(`${si}-${fi}-operator`);
                                  }}
                                  renderValue={(val) =>
                                    val ? (
                                      getEnumLabel(
                                        Object.entries(
                                          typeof getAttributeOperator(
                                            filter.attribute,
                                          ) === "object" &&
                                            !Array.isArray(
                                              getAttributeOperator(
                                                filter.attribute,
                                              ),
                                            )
                                            ? getAttributeOperator(
                                                filter.attribute,
                                              )
                                            : {},
                                        ).find(([, v]) => v === val)?.[0] ??
                                          (Array.isArray(
                                            getAttributeOperator(
                                              filter.attribute,
                                            ),
                                          )
                                            ? (
                                                getAttributeOperator(
                                                  filter.attribute,
                                                ) as [string, string][]
                                              ).find(([, v]) => v === val)?.[0]
                                            : undefined) ??
                                          val,
                                        getAttributeDataType(
                                          filter.attribute,
                                        ) ?? undefined,
                                      )
                                    ) : (
                                      <Box
                                        component="span"
                                        sx={{ color: gray[400] }}
                                      >
                                        Select
                                      </Box>
                                    )
                                  }
                                  sx={{ borderRadius: "8px" }}
                                  error={!!fieldErrors[`${si}-${fi}-operator`]}
                                >
                                  {filter.attribute &&
                                    (() => {
                                      const ops = getAttributeOperator(
                                        filter.attribute,
                                      );
                                      if (!ops) return null;
                                      const entries: [string, string][] =
                                        Array.isArray(ops)
                                          ? ops
                                          : Object.entries(ops);
                                      return entries.map(([key, value]) => (
                                        <MenuItem key={key} value={value}>
                                          {getEnumLabel(
                                            key,
                                            getAttributeDataType(
                                              filter.attribute,
                                            ) ?? undefined,
                                          )}
                                        </MenuItem>
                                      ));
                                    })()}
                                </Select>
                                {fieldErrors[`${si}-${fi}-operator`] && (
                                  <Typography variant="caption" color="error">
                                    {fieldErrors[`${si}-${fi}-operator`]}
                                  </Typography>
                                )}
                              </Stack>
                            </Box>

                            {/* Value */}
                            <Stack spacing={0.5}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Value
                              </Typography>
                              <Box display="flex" alignItems="center" gap={1}>
                                <Box flex={1}>
                                  <DynamicFieldRenderer
                                    key={`${si}-${fi}-${filter.attribute}-${getAttributeDataType(filter.attribute)}`}
                                    config={{
                                      dataType:
                                        getAttributeDataType(
                                          filter.attribute,
                                        ) ?? "string",
                                      attributeKey: filter.attribute,
                                      attributeName: getAttributeName(
                                        filter.attribute,
                                      ),
                                    }}
                                    value={filter.value}
                                    options={getAttributeOptions(
                                      filter.attribute,
                                    )}
                                    onChange={(newValue) => {
                                      handleChange(
                                        si,
                                        fi,
                                        "value",
                                        String(newValue ?? ""),
                                      );
                                      clearFieldError(`${si}-${fi}-value`);
                                    }}
                                    mode="filter"
                                    usePipeSeparatedArrays
                                    operator={filter.operator as any}
                                    openDatepicker={
                                      openDatepickerIndex === `${si}-${fi}`
                                    }
                                    onOpenDatepicker={() =>
                                      setOpenDatepickerIndex(`${si}-${fi}`)
                                    }
                                    onCloseDatepicker={() =>
                                      setOpenDatepickerIndex(null)
                                    }
                                    openStartDatepicker={openStartDatepicker}
                                    onOpenStartDatepicker={() =>
                                      setOpenStartDatepicker(true)
                                    }
                                    onCloseStartDatepicker={() =>
                                      setOpenStartDatepicker(false)
                                    }
                                    openEndDatepicker={openEndDatepicker}
                                    onOpenEndDatepicker={() =>
                                      setOpenEndDatepicker(true)
                                    }
                                    onCloseEndDatepicker={() =>
                                      setOpenEndDatepicker(false)
                                    }
                                  />
                                </Box>
                                {getAttributeDataType(filter.attribute) ===
                                  "string" && (
                                  <IconButton
                                    size="small"
                                    disabled={!filter.operator}
                                    onClick={() =>
                                      getValuesForAttribute(
                                        filter.attribute,
                                        filter.operator,
                                        filter.value,
                                        si,
                                        fi,
                                      )
                                    }
                                  >
                                    <VisibilityIcon fontSize="small" />
                                  </IconButton>
                                )}
                              </Box>
                              {fieldErrors[`${si}-${fi}-value`] && (
                                <Typography variant="caption" color="error">
                                  {fieldErrors[`${si}-${fi}-value`]}
                                </Typography>
                              )}
                            </Stack>
                          </Stack>
                        </Box>
                      ))}
                    </Stack>

                    {/* Add filter button */}
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={() => addRow(si)}
                      disabled={
                        getAttributeDataType(
                          selection.filters[selection.filters.length - 1]
                            ?.attribute,
                        ) === "boolean"
                      }
                      sx={{
                        borderRadius: "8px",
                        borderColor: brand[600],
                        color: brand[600],
                        textTransform: "none",
                        fontWeight: 600,
                      }}
                    >
                      Add filter
                    </Button>

                    {/* OR divider */}
                    <Divider>
                      <Typography variant="body2" color="text.secondary">
                        OR
                      </Typography>
                    </Divider>
                  </React.Fragment>
                ))}

                {/* Add new group */}
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={addSelection}
                  sx={{
                    borderRadius: "8px",
                    borderColor: gray[700],
                    color: gray[700],
                    textTransform: "none",
                    fontWeight: 600,
                  }}
                >
                  Add new group
                </Button>
              </Stack>
            )}
          </Box>

          {/* Footer */}
          <Divider />
          <Box display="flex" gap={1.5} p={2}>
            {loading ? (
              <CircularProgress size={36} />
            ) : (
              <Button
                onClick={handleApply}
                sx={{
                  borderRadius: "8px",
                  backgroundColor: `${brand[600]} !important`,
                  color: "white",
                  textTransform: "none",
                  fontWeight: 600,
                  "&:hover": { bgcolor: `${brand[700]} !important` },
                }}
              >
                Apply filters
              </Button>
            )}
            <Button
              variant="outlined"
              disabled={!hasFilterAnyValue}
              onClick={() => {
                clearAll();
                setFieldErrors({});
              }}
              sx={{
                borderRadius: "8px",
                borderColor: brand[600],
                color: brand[600],
                textTransform: "none",
                fontWeight: 600,
                "&.Mui-disabled": {
                  borderColor: gray[300],
                  color: gray[400],
                },
              }}
            >
              Clear all
            </Button>
          </Box>
        </Box>
      )}
    </Drawer>
  );
};
